import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2, Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectClosureForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const savedId = searchParams.get('saved');
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = savedId;

  const getEmptyForm = () => ({
    project_name: "",
    client_name: "",
    project_manager: "",
    start_date: "",
    end_date: new Date().toISOString().split("T")[0],
    executive_summary: "",
    objectives_achieved: [{ objective: "", status: "Achieved", notes: "" }],
    deliverables: [{ deliverable: "", status: "Delivered", acceptance_date: "" }],
    budget_summary: { planned_budget: 0, actual_cost: 0, variance: 0, variance_explanation: "" },
    timeline_summary: { planned_duration: "", actual_duration: "", variance: "" },
    lessons_learned: [""],
    recommendations: [""],
    sign_off: [{ name: "", role: "", date: "" }]
  });

  const getSampleData = () => ({
    project_name: "Enterprise CRM Implementation",
    client_name: "ACME Corporation",
    project_manager: "John Smith",
    start_date: "2024-06-01",
    end_date: new Date().toISOString().split("T")[0],
    executive_summary: "The Enterprise CRM Implementation project has been successfully completed. All primary objectives were achieved, with the system going live on schedule. The project delivered a comprehensive CRM solution that integrates with existing ERP systems, providing sales, marketing, and customer service teams with a unified platform for customer engagement.",
    objectives_achieved: [
      { objective: "Implement core CRM functionality", status: "Achieved", notes: "All modules deployed successfully" },
      { objective: "Integrate with existing ERP system", status: "Achieved", notes: "Real-time bi-directional sync operational" },
      { objective: "Train 500+ end users", status: "Achieved", notes: "98% training completion rate" },
      { objective: "Achieve 90% user adoption in 3 months", status: "Partially Achieved", notes: "Current adoption at 85%, improvement plan in place" }
    ],
    deliverables: [
      { deliverable: "CRM Core Platform", status: "Delivered", acceptance_date: "2025-01-10" },
      { deliverable: "ERP Integration Module", status: "Delivered", acceptance_date: "2025-01-12" },
      { deliverable: "Mobile Application", status: "Delivered", acceptance_date: "2025-01-15" },
      { deliverable: "User Training Materials", status: "Delivered", acceptance_date: "2024-12-20" },
      { deliverable: "System Documentation", status: "Delivered", acceptance_date: "2025-01-18" }
    ],
    budget_summary: {
      planned_budget: 500000,
      actual_cost: 485000,
      variance: 15000,
      variance_explanation: "Under budget due to efficient resource utilization and early completion of development phase"
    },
    timeline_summary: {
      planned_duration: "8 months",
      actual_duration: "7.5 months",
      variance: "2 weeks ahead of schedule"
    },
    lessons_learned: [
      "Early stakeholder engagement significantly improved requirements accuracy",
      "Agile methodology allowed for flexible scope management",
      "Dedicated integration testing environment prevented production issues",
      "Regular client demos reduced change requests in later phases",
      "Cross-training team members improved resilience and knowledge transfer"
    ],
    recommendations: [
      "Establish a Center of Excellence for ongoing CRM optimization",
      "Schedule quarterly user feedback sessions to drive continuous improvement",
      "Plan Phase 2 enhancements including advanced analytics and AI features",
      "Develop formal change management process for future system updates",
      "Create power user program to support adoption in remaining departments"
    ],
    sign_off: [
      { name: "John Smith", role: "Project Manager", date: new Date().toISOString().split("T")[0] },
      { name: "Sarah Johnson", role: "IT Director (Client)", date: "" },
      { name: "Mike Chen", role: "Business Sponsor", date: "" }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Project Closure Report Draft";
      const payload = {
        name: draftName,
        template_type: "Project Closure Report",
        template_id: "project-closure",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  useEffect(() => {
    // Load saved template if editing
    if (savedId) {
      const loadSavedTemplate = async () => {
        try {
          const response = await axios.get(`${API}/saved-templates/${savedId}`);
          if (response.data && response.data.data) {
            setFormData(response.data.data);
          }
        } catch (error) {
          console.error("Error loading saved template:", error);
        }
      };
      loadSavedTemplate();
    }
  }, [savedId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.project_name || "Untitled Project Closure",
        template_type: "Project Closure Report",
        template_id: "project-closure",
        format: "DOCX",
        data: formData
      };
      
      if (savedId) {
        await axios.put(`${API}/saved-templates/${savedId}`, payload);
        alert("Template updated successfully!");
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload);
        alert("Template saved successfully!");
        navigate(`/template/project-closure?saved=${response.data.id}`);
      }
    } catch (error) {
      console.error("Error saving:", error);
      alert("Error saving template.");
    }
    setSaving(false);
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "project-closure", "Project Closure", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("project-closure", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/project-closure`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Project_Closure_${formData.project_name || "Document"}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Project Closure Report",
        formData,
        settings: activeSettings || {},
        templateType: "Project Closure Report",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateObjective = (index, field, value) => {
    const updated = [...formData.objectives_achieved];
    updated[index][field] = value;
    setFormData({ ...formData, objectives_achieved: updated });
  };

  const addObjective = () => {
    setFormData({
      ...formData,
      objectives_achieved: [...formData.objectives_achieved, { objective: "", status: "Achieved", notes: "" }]
    });
  };

  const removeObjective = (index) => {
    setFormData({ ...formData, objectives_achieved: formData.objectives_achieved.filter((_, i) => i !== index) });
  };

  const updateDeliverable = (index, field, value) => {
    const updated = [...formData.deliverables];
    updated[index][field] = value;
    setFormData({ ...formData, deliverables: updated });
  };

  const addDeliverable = () => {
    setFormData({
      ...formData,
      deliverables: [...formData.deliverables, { deliverable: "", status: "Delivered", acceptance_date: "" }]
    });
  };

  const removeDeliverable = (index) => {
    setFormData({ ...formData, deliverables: formData.deliverables.filter((_, i) => i !== index) });
  };

  const updateListItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addListItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeListItem = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const updateSignOff = (index, field, value) => {
    const updated = [...formData.sign_off];
    updated[index][field] = value;
    setFormData({ ...formData, sign_off: updated });
  };

  const addSignOff = () => {
    setFormData({
      ...formData,
      sign_off: [...formData.sign_off, { name: "", role: "", date: "" }]
    });
  };

  const removeSignOff = (index) => {
    setFormData({ ...formData, sign_off: formData.sign_off.filter((_, i) => i !== index) });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Project Closure Report</h1>
                <p className="text-sm text-gray-500">Document project completion and lessons learned</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Project Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Client Name</Label>
                <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Client name" />
              </div>
              <div>
                <Label>Project Manager</Label>
                <Input value={formData.project_manager} onChange={(e) => setFormData({ ...formData, project_manager: e.target.value })} placeholder="PM name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                </div>
              </div>
            </div>

            <div>
              <Label>Executive Summary</Label>
              <Textarea value={formData.executive_summary} onChange={(e) => setFormData({ ...formData, executive_summary: e.target.value })} placeholder="High-level summary of project completion..." rows={4} />
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Objectives Achieved</Label>
              {formData.objectives_achieved.map((obj, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label>Objective</Label>
                      <Input value={obj.objective} onChange={(e) => updateObjective(index, "objective", e.target.value)} placeholder="Objective description" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={obj.status} onValueChange={(v) => updateObjective(index, "status", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Achieved">Achieved</SelectItem>
                          <SelectItem value="Partially Achieved">Partially Achieved</SelectItem>
                          <SelectItem value="Not Achieved">Not Achieved</SelectItem>
                          <SelectItem value="Deferred">Deferred</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <Input value={obj.notes} onChange={(e) => updateObjective(index, "notes", e.target.value)} placeholder="Notes" />
                      <Button variant="ghost" size="icon" onClick={() => removeObjective(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addObjective}>
                <Plus className="h-4 w-4 mr-2" /> Add Objective
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Deliverables</Label>
              {formData.deliverables.map((del, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label>Deliverable</Label>
                      <Input value={del.deliverable} onChange={(e) => updateDeliverable(index, "deliverable", e.target.value)} placeholder="Deliverable name" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={del.status} onValueChange={(v) => updateDeliverable(index, "status", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Delivered">Delivered</SelectItem>
                          <SelectItem value="Partial">Partial</SelectItem>
                          <SelectItem value="Not Delivered">Not Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <Input type="date" value={del.acceptance_date} onChange={(e) => updateDeliverable(index, "acceptance_date", e.target.value)} />
                      <Button variant="ghost" size="icon" onClick={() => removeDeliverable(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addDeliverable}>
                <Plus className="h-4 w-4 mr-2" /> Add Deliverable
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Budget Summary</Label>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Planned Budget</Label>
                  <Input type="number" value={formData.budget_summary.planned_budget} onChange={(e) => setFormData({ ...formData, budget_summary: { ...formData.budget_summary, planned_budget: parseFloat(e.target.value) || 0 } })} />
                </div>
                <div>
                  <Label>Actual Cost</Label>
                  <Input type="number" value={formData.budget_summary.actual_cost} onChange={(e) => setFormData({ ...formData, budget_summary: { ...formData.budget_summary, actual_cost: parseFloat(e.target.value) || 0 } })} />
                </div>
                <div>
                  <Label>Variance</Label>
                  <Input type="number" value={formData.budget_summary.variance} onChange={(e) => setFormData({ ...formData, budget_summary: { ...formData.budget_summary, variance: parseFloat(e.target.value) || 0 } })} />
                </div>
                <div>
                  <Label>Variance Explanation</Label>
                  <Input value={formData.budget_summary.variance_explanation} onChange={(e) => setFormData({ ...formData, budget_summary: { ...formData.budget_summary, variance_explanation: e.target.value } })} placeholder="Explanation" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Timeline Summary</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Planned Duration</Label>
                  <Input value={formData.timeline_summary.planned_duration} onChange={(e) => setFormData({ ...formData, timeline_summary: { ...formData.timeline_summary, planned_duration: e.target.value } })} placeholder="8 months" />
                </div>
                <div>
                  <Label>Actual Duration</Label>
                  <Input value={formData.timeline_summary.actual_duration} onChange={(e) => setFormData({ ...formData, timeline_summary: { ...formData.timeline_summary, actual_duration: e.target.value } })} placeholder="7.5 months" />
                </div>
                <div>
                  <Label>Variance</Label>
                  <Input value={formData.timeline_summary.variance} onChange={(e) => setFormData({ ...formData, timeline_summary: { ...formData.timeline_summary, variance: e.target.value } })} placeholder="2 weeks ahead" />
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="font-semibold">Lessons Learned</Label>
              {formData.lessons_learned.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={item} onChange={(e) => updateListItem("lessons_learned", index, e.target.value)} placeholder="Lesson learned" />
                  <Button variant="ghost" size="icon" onClick={() => removeListItem("lessons_learned", index)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addListItem("lessons_learned")}>
                <Plus className="h-4 w-4 mr-2" /> Add Lesson
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Recommendations</Label>
              {formData.recommendations.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={item} onChange={(e) => updateListItem("recommendations", index, e.target.value)} placeholder="Recommendation" />
                  <Button variant="ghost" size="icon" onClick={() => removeListItem("recommendations", index)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addListItem("recommendations")}>
                <Plus className="h-4 w-4 mr-2" /> Add Recommendation
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Sign-Off</Label>
              {formData.sign_off.map((signer, index) => (
                <div key={index} className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={signer.name} onChange={(e) => updateSignOff(index, "name", e.target.value)} placeholder="Name" />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Input value={signer.role} onChange={(e) => updateSignOff(index, "role", e.target.value)} placeholder="Role" />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={signer.date} onChange={(e) => updateSignOff(index, "date", e.target.value)} />
                  </div>
                  <div className="flex items-end">
                    <Button variant="ghost" size="icon" onClick={() => removeSignOff(index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addSignOff}>
                <Plus className="h-4 w-4 mr-2" /> Add Signer
              </Button>
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={handleSave} disabled={saving || !formData.project_name} className="flex-1" size="lg" data-testid="save-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.project_name} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                <FileText className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProjectClosureForm;
